import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { API_URL, formatDate, getStatusBadgeClass } from '../lib/utils';
import { toast } from 'sonner';
import {
  Briefcase,
  Users,
  FileText,
  Loader2,
  Building2,
  UserCheck,
  TrendingUp,
  DollarSign,
  Settings,
  CreditCard,
  Shield,
  Upload,
  Rss,
  FileSpreadsheet,
  Download,
  CheckCircle2,
  AlertCircle,
  BarChart3,
  Mail,
  MessageSquare,
  MousePointerClick,
  Calendar,
  ArrowUpRight,
  ArrowDownRight,
  Send,
  Clock,
  BadgeCheck,
  ExternalLink,
  XCircle,
  MapPin,
  Bell,
  Eye,
} from 'lucide-react';
import { useAdminCtx } from './AdminContext';


const RANGE_TABS = [['today', 'Today'], ['yesterday', 'Yesterday'], ['7d', '7 days'], ['30d', '30 days'], ['custom', 'Custom']];

const Info = ({ text }) => (
  <span className="group relative inline-flex align-middle ml-1 cursor-help" tabIndex={0}>
    <span className="w-3.5 h-3.5 rounded-full bg-gray-300 text-white text-[9px] font-bold flex items-center justify-center">i</span>
    <span className="pointer-events-none absolute left-1/2 -translate-x-1/2 bottom-full mb-1 w-56 rounded-lg bg-gray-900 text-white text-[11px] leading-snug p-2 opacity-0 group-hover:opacity-100 group-focus:opacity-100 transition-opacity z-30 shadow-lg">{text}</span>
  </span>
);

const Stat = ({ label, value, def, active, onClick, testid }) => (
  <button type="button" onClick={onClick} data-testid={testid}
    className={`text-left bg-white rounded-lg p-3 border transition-all ${active ? 'border-primary ring-2 ring-primary/30' : 'border-gray-100 hover:border-primary/40'}`}>
    <p className="text-2xl font-bold text-primary tabular-nums">{(value ?? 0).toLocaleString()}</p>
    <p className="text-xs text-gray-500 flex items-center">{label}{def && <Info text={def} />}</p>
  </button>
);

const BreakdownList = ({ title, rows, labelKey, max }) => (
  <div className="bg-white rounded-xl p-5 border border-gray-100">
    <h4 className="font-semibold text-gray-900 text-sm mb-3">{title}</h4>
    {rows && rows.length ? (
      <div className="space-y-1.5">
        {rows.slice(0, max || 10).map((r, i) => {
          const total = rows.reduce((s, x) => s + (x.count || x.pageviews || 0), 0) || 1;
          const v = r.count ?? r.pageviews ?? 0;
          return (
            <div key={i} className="text-sm" data-testid={`bd-${title}-${i}`}>
              <div className="flex items-center justify-between">
                <span className="truncate text-gray-700 pr-2">{labelKey(r)}</span>
                <span className="shrink-0 font-semibold text-primary tabular-nums">{v.toLocaleString()}</span>
              </div>
              <div className="h-1 bg-gray-100 rounded mt-0.5"><div className="h-1 bg-secondary rounded" style={{ width: `${Math.round(v / total * 100)}%` }} /></div>
            </div>
          );
        })}
      </div>
    ) : <p className="text-sm text-gray-400">No data for this range yet.</p>}
  </div>
);

const AnalyticsPanel = ({ siteVisits }) => {
  const [range, setRange] = useState('30d');
  const [custom, setCustom] = useState({ start: '', end: '' });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [drill, setDrill] = useState('sources');

  useEffect(() => {
    if (range === 'custom' && (!custom.start || !custom.end)) return;
    setLoading(true);
    const qs = range === 'custom' ? `range=custom&start=${custom.start}&end=${custom.end}` : `range=${range}`;
    axios.get(`${API_URL}/admin/analytics/overview?${qs}`)
      .then((r) => setData(r.data)).catch(() => toast.error('Could not load analytics'))
      .finally(() => setLoading(false));
  }, [range, custom]);

  const t = data?.totals || {};
  return (
    <div className="bg-gradient-to-br from-blue-50 to-white rounded-xl p-6 border border-blue-200 md:col-span-2" data-testid="analytics-panel">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <BarChart3 className="w-5 h-5 text-blue-600" />
          <h3 className="font-semibold text-gray-900">Website Traffic</h3>
          <span className="text-xs text-gray-500">· {data?.timezone || 'Central Time'}</span>
        </div>
        <div className="flex items-center gap-1 flex-wrap">
          {RANGE_TABS.map(([k, lbl]) => (
            <button key={k} onClick={() => setRange(k)} data-testid={`range-${k}`}
              className={`text-xs px-3 py-1.5 rounded-full font-medium transition-colors ${range === k ? 'bg-primary text-white' : 'bg-white border text-gray-600 hover:border-primary/40'}`}>{lbl}</button>
          ))}
        </div>
      </div>

      {range === 'custom' && (
        <div className="flex items-center gap-2 mb-3 text-sm" data-testid="custom-range">
          <input type="date" value={custom.start} onChange={(e) => setCustom((c) => ({ ...c, start: e.target.value }))} className="border rounded px-2 py-1" />
          <span>to</span>
          <input type="date" value={custom.end} onChange={(e) => setCustom((c) => ({ ...c, end: e.target.value }))} className="border rounded px-2 py-1" />
        </div>
      )}

      <p className="text-xs text-gray-500 mb-3">{data?.label}{data?.start && ` (${data.start} → ${data.end})`}</p>

      {loading ? <div className="py-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div> : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 mb-4">
            <Stat testid="stat-pageviews" label="Pageviews" value={t.pageviews} def={data?.definitions?.pageviews} active={drill === 'pages'} onClick={() => setDrill('pages')} />
            <Stat testid="stat-unique" label="Unique visitors" value={t.unique_visitors} def={data?.definitions?.unique_visitors} active={drill === 'locations'} onClick={() => setDrill('locations')} />
            <Stat testid="stat-sessions" label="Sessions" value={t.sessions} def={data?.definitions?.sessions} active={false} onClick={() => setDrill('sources')} />
            <Stat testid="stat-new" label="New" value={t.new_visitors} def={data?.definitions?.new_vs_returning} active={false} onClick={() => setDrill('sources')} />
            <Stat testid="stat-returning" label="Returning" value={t.returning_visitors} def={data?.definitions?.new_vs_returning} active={false} onClick={() => setDrill('sources')} />
            <Stat testid="stat-signups" label="Signups" value={t.signups} def="New accounts created in this range." active={false} onClick={() => setDrill('sources')} />
          </div>

          <div className="flex items-center gap-1 mb-3 flex-wrap">
            {[['sources', 'Sources'], ['sections', 'Sections'], ['pages', 'Top pages'], ['locations', 'Locations'], ['searches', 'Searches']].map(([k, lbl]) => (
              <button key={k} onClick={() => setDrill(k)} data-testid={`drill-${k}`}
                className={`text-xs px-3 py-1 rounded-full font-medium ${drill === k ? 'bg-secondary text-white' : 'bg-white border text-gray-600'}`}>{lbl}</button>
            ))}
          </div>

          {drill === 'sources' && <BreakdownList title="Traffic sources" rows={data?.sources} labelKey={(r) => r.source} max={12} />}
          {drill === 'sections' && <BreakdownList title="Sections (pageviews — can overlap by visitor)" rows={data?.sections} labelKey={(r) => r.section} max={12} />}
          {drill === 'pages' && <BreakdownList title="Top pages" rows={data?.top_pages} labelKey={(r) => r.path} max={15} />}
          {drill === 'locations' && <BreakdownList title="Approximate locations" rows={data?.top_locations} labelKey={(r) => [r.city, r.region, r.country].filter(Boolean).join(', ') || 'Unknown'} max={15} />}
          {drill === 'searches' && (
            <div className="grid sm:grid-cols-2 gap-3">
              <BreakdownList title="Top searches" rows={data?.top_searches} labelKey={(r) => r.query} max={12} />
              <BreakdownList title="Searches with no results" rows={data?.no_result_searches} labelKey={(r) => r.query} max={12} />
            </div>
          )}

          {data?.definitions && (
            <p className="text-xs text-gray-500 mt-3 bg-amber-50 border border-amber-100 rounded p-2">
              <strong>Note:</strong> {data?.definitions?.sources}
            </p>
          )}
          {data?.coverage?.note && (
            <p className="text-xs text-gray-400 mt-2">{data.coverage.note}</p>
          )}

          {/* Logins today (from existing login tracking) */}
          <div className="grid grid-cols-3 gap-2 mt-4" data-testid="today-logins">
            {[['Logins today', siteVisits?.today_logins], ['Employer', siteVisits?.today_employer_logins], ['Social worker', siteVisits?.today_seeker_logins]].map(([l, v], i) => (
              <div key={i} className="bg-white rounded-lg p-3 text-center border border-gray-100">
                <p className="text-xl font-bold text-primary tabular-nums">{v ?? 0}</p>
                <p className="text-xs text-gray-500">{l}</p>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};


const AdminTabOverview = () => {
  const { prioritySignups, signupsByState, siteVisits, subscriptions, users } = useAdminCtx();
  const navigate = useNavigate();
  const [resourcePending, setResourcePending] = useState(0);
  const [contentStats, setContentStats] = useState(null);
  useEffect(() => {
    axios.get(`${API_URL}/admin/resources/review-queue`)
      .then((r) => setResourcePending(r.data?.counts?.pending_review || 0))
      .catch(() => {});
    axios.get(`${API_URL}/content-stats`)
      .then((r) => setContentStats(r.data))
      .catch(() => {});
  }, []);
  const lastRefreshRaw = contentStats?.last_refresh || contentStats?.jobs_last_synced || null;
  const lastRefreshText = lastRefreshRaw
    ? new Date(String(lastRefreshRaw).length <= 10 ? `${lastRefreshRaw}T08:00:00Z` : lastRefreshRaw)
        .toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })
    : 'No refresh recorded yet';
  return (
    <>
            {/* Live content freshness — the source of truth behind the homepage job count */}
            <div className="mb-6 bg-white rounded-xl p-5 border border-gray-100 flex flex-wrap items-center gap-x-8 gap-y-3" data-testid="content-freshness-card">
              <div className="flex items-center gap-2">
                <Briefcase className="w-5 h-5 text-primary" />
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide">Active Jobs (live)</p>
                  <p className="text-xl font-bold text-primary" data-testid="freshness-active-jobs">
                    {contentStats && typeof contentStats.active_jobs_total === 'number' ? contentStats.active_jobs_total.toLocaleString() : '—'}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-secondary" />
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide">Last Content Refresh</p>
                  <p className="text-sm font-semibold text-gray-800" data-testid="freshness-last-refresh">{lastRefreshText}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {contentStats?.jobs_updated_today
                  ? <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                  : <AlertCircle className="w-5 h-5 text-amber-500" />}
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide">Daily 8 AM Sync</p>
                  <p className="text-sm font-semibold text-gray-800" data-testid="freshness-sync-today">
                    {contentStats?.jobs_updated_today ? 'Ran today' : 'Not yet run today'}
                  </p>
                </div>
              </div>
              <p className="text-xs text-gray-400 basis-full">Homepage always shows this live active-job count with today's date; the count updates on every request.</p>
            </div>

            <div className="flex flex-wrap gap-2 mb-6" data-testid="admin-quicklinks">
              <button onClick={() => navigate('/admin/marketing')} data-testid="quicklink-marketing" className="px-4 py-2 rounded-lg text-sm font-semibold bg-primary text-white hover:bg-primary-hover">Marketing &amp; Acquisition</button>
              <button onClick={() => navigate('/admin/coverage')} data-testid="quicklink-coverage" className="px-4 py-2 rounded-lg text-sm font-semibold bg-white border text-gray-700 hover:border-primary/40">Resource Coverage Map</button>
              <button onClick={() => navigate('/admin/featured')} data-testid="quicklink-featured" className="px-4 py-2 rounded-lg text-sm font-semibold bg-white border text-gray-700 hover:border-primary/40">Featured Program</button>
              <button onClick={() => navigate('/admin/resources')} data-testid="quicklink-resources" className="px-4 py-2 rounded-lg text-sm font-semibold bg-white border text-gray-700 hover:border-primary/40 flex items-center gap-2">
                Manage Resources &amp; Review Queue
                {resourcePending > 0 && (
                  <span data-testid="resource-pending-badge" className="inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full bg-secondary text-white text-xs font-bold">{resourcePending}</span>
                )}
              </button>
            </div>
            <div className="grid lg:grid-cols-2 gap-6">
              {/* Priority Signups Widget - Candidate Leads */}
              <div className="bg-gradient-to-br from-secondary/10 to-secondary/5 rounded-xl p-6 border border-secondary/20">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <Bell className="w-5 h-5 text-secondary" />
                    <h3 className="font-semibold text-gray-900">Priority Candidate Leads</h3>
                  </div>
                  <Badge className="bg-secondary/20 text-secondary border-secondary/30">
                    {prioritySignups.length} Total
                  </Badge>
                </div>
                
                {Object.keys(signupsByState).length > 0 ? (
                  <div className="space-y-2">
                    {Object.entries(signupsByState)
                      .sort((a, b) => b[1].length - a[1].length)
                      .slice(0, 6)
                      .map(([state, signups]) => (
                        <div key={state} className="flex justify-between items-center p-2 bg-white rounded-lg">
                          <div className="flex items-center gap-2">
                            <MapPin className="w-4 h-4 text-primary" />
                            <span className="text-sm font-medium text-gray-700">{state}</span>
                          </div>
                          <span className="text-sm font-bold text-secondary">{signups.length} candidates</span>
                        </div>
                    ))}
                    {Object.keys(signupsByState).length > 6 && (
                      <p className="text-xs text-gray-500 text-center pt-2">
                        +{Object.keys(signupsByState).length - 6} more states
                      </p>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-4">
                    <MapPin className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                    <p className="text-gray-500 text-sm">No priority signups yet</p>
                    <p className="text-xs text-gray-400">Candidates sign up from category pages</p>
                  </div>
                )}
                
                <div className="mt-4 pt-4 border-t border-secondary/20">
                  <p className="text-xs text-gray-600">
                    <strong>Sales Pitch:</strong> "We have {prioritySignups.length} candidates waiting for vetted positions."
                  </p>
                </div>
              </div>

              {/* Website Traffic — unified analytics (Central Time, sources, sections, drill-down) */}
              <AnalyticsPanel siteVisits={siteVisits} />

              <div className="bg-white rounded-xl p-6 border border-gray-100">
                <h3 className="font-semibold text-gray-900 mb-4">Recent Subscriptions</h3>
                <div className="space-y-3">
                  {subscriptions.slice(0, 5).map((sub) => (
                    <div key={sub.subscription_id} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                      <div>
                        <p className="font-medium text-gray-900">{sub.plan_name}</p>
                        <p className="text-sm text-gray-500">${sub.amount}</p>
                      </div>
                      <Badge className={getStatusBadgeClass(sub.status)}>
                        {sub.status}
                      </Badge>
                    </div>
                  ))}
                  {subscriptions.length === 0 && (
                    <p className="text-gray-500 text-center py-4">No subscriptions yet</p>
                  )}
                </div>
              </div>

              <div className="bg-white rounded-xl p-6 border border-gray-100">
                <h3 className="font-semibold text-gray-900 mb-4">Recent Users</h3>
                <div className="space-y-3">
                  {users.slice(0, 5).map((u) => (
                    <div key={u.user_id} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                      <div>
                        <p className="font-medium text-gray-900">{u.name}</p>
                        <p className="text-sm text-gray-500">{u.email}</p>
                      </div>
                      <Badge className={getStatusBadgeClass(u.user_type === 'job_seeker' ? 'info' : u.user_type === 'employer' ? 'active' : 'pending')}>
                        {u.user_type}
                      </Badge>
                    </div>
                  ))}
                </div>
              </div>

              {/* Recent Priority Signups List */}
              <div className="bg-white rounded-xl p-6 border border-gray-100">
                <h3 className="font-semibold text-gray-900 mb-4">Recent Candidate Leads</h3>
                <div className="space-y-3">
                  {prioritySignups.slice(0, 5).map((signup) => (
                    <div key={signup.signup_id} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                      <div>
                        <p className="font-medium text-gray-900">{signup.email}</p>
                        <p className="text-sm text-gray-500">{signup.specialty || signup.category || 'General'}</p>
                      </div>
                      {signup.state && (
                        <Badge className="bg-primary/10 text-primary border-primary/20">
                          {signup.state}
                        </Badge>
                      )}
                    </div>
                  ))}
                  {prioritySignups.length === 0 && (
                    <p className="text-gray-500 text-center py-4">No candidate leads yet</p>
                  )}
                </div>
              </div>
            </div>
    </>
  );
};

export default AdminTabOverview;
